// CsvPreview (E7.2.1) — busca o CSV (url assinada do dispatcher), parseia in-repo
// (sem dep nova) e renderiza uma tabela rolável com teto de linhas.
//
// `fetch(url)` direto: a url do dispatcher é um proxy assinado (`?sig=`) que
// autentica sem Bearer — igual ao `<img src>` do chat (dev proxia `/api`).
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

export interface CsvPreviewProps {
  url: string;
  filename?: string;
}

/** Teto de linhas renderizadas (preview ≠ planilha completa). */
export const CSV_MAX_ROWS = 500;

/**
 * Detecta o delimitador pela 1ª linha (ignorando o que está entre aspas):
 * vírgula, ponto-e-vírgula (comum em CSV BR/Excel) ou tab. Default `,`.
 */
export function detectDelimiter(sample: string): string {
  const firstLine = sample.split(/\r?\n/, 1)[0] ?? "";
  const counts: Record<string, number> = { ",": 0, ";": 0, "\t": 0 };
  let inQuotes = false;
  for (const ch of firstLine) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && ch in counts) counts[ch] += 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best && best[1] > 0 ? best[0] : ",";
}

/**
 * Parser CSV RFC4180-ish: campos entre aspas, escape de aspas (`""`), quebras
 * CRLF/LF, delimitador configurável. Devolve linhas de strings.
 */
export function parseCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;
  const n = text.length;
  while (i < n) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === delimiter) {
      row.push(field);
      field = "";
      i += 1;
      continue;
    }
    if (ch === "\r" || ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      // CRLF conta como UMA quebra; CR sozinho (Mac clássico) e LF também.
      i += ch === "\r" && text[i + 1] === "\n" ? 2 : 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  // Último campo/linha quando o arquivo não termina em quebra.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

interface CsvState {
  loading: boolean;
  error: boolean;
  rows: string[][];
  total: number;
}

export function CsvPreview({ url }: CsvPreviewProps) {
  const [state, setState] = useState<CsvState>({
    loading: true,
    error: false,
    rows: [],
    total: 0,
  });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: false }));
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((raw) => {
        if (!alive) return;
        // Excel exporta UTF-8 com BOM (﻿) — tira pra não sujar o 1º header.
        const text = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
        const all = parseCsv(text, detectDelimiter(text)).filter(
          (r) => r.length > 1 || (r[0] ?? "").length > 0
        );
        setState({
          loading: false,
          error: false,
          rows: all.slice(0, CSV_MAX_ROWS),
          total: all.length,
        });
      })
      .catch(() => {
        if (alive) setState({ loading: false, error: true, rows: [], total: 0 });
      });
    return () => {
      alive = false;
    };
  }, [url]);

  if (state.loading)
    return (
      <div
        className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground"
        data-testid="csv-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando CSV…
      </div>
    );
  if (state.error)
    return (
      <div className="py-8 text-center text-sm text-destructive" data-testid="csv-error">
        Erro ao carregar o CSV.
      </div>
    );
  if (state.rows.length === 0)
    return <div className="py-8 text-center text-sm text-muted-foreground">Arquivo CSV vazio.</div>;

  const [header, ...body] = state.rows;
  // Largura = MAIOR linha (CSV ragged: dados podem ter + colunas que o header).
  const colCount = state.rows.reduce((m, r) => Math.max(m, r.length), 0);
  const cols = Array.from({ length: colCount });
  return (
    <div className="w-full" data-testid="csv-preview">
      <div className="max-h-[70vh] overflow-auto rounded border">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-muted">
            <tr>
              {cols.map((_, i) => (
                <th key={i} scope="col" className="border px-2 py-1 text-left font-medium">
                  {header[i] ?? ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((r, ri) => (
              <tr key={ri} className="odd:bg-background even:bg-muted/30">
                {cols.map((_, ci) => (
                  <td key={ci} className="border px-2 py-1">
                    {r[ci] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {state.total > state.rows.length && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="csv-rowcap">
          Mostrando {state.rows.length} de {state.total} linhas. Baixe o arquivo para ver tudo.
        </p>
      )}
    </div>
  );
}

export default CsvPreview;
